import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, Type, inject } from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ListRowComponent } from '../../ui-components/list-row/list-row.component';
import { CheckIconComponent } from '../../../@icons/check-icon.component';
import { CheckCheckIconComponent } from '../../../@icons/check-check-icon.component';
import { BasketIconComponent } from '../../../@icons/basket-icon.component';
import { LeafOffIconComponent } from '../../../@icons/leaf-off-icon.component';
import { PencilIconComponent } from '../../../@icons/pencil-icon.component';
import { NounForm, formKey } from '../../utils/noun-phrase.util';

/**
 * Les réponses à l'assistant. `DONE` : je viens de le faire ; `ALREADY_DONE` :
 * c'était fait avant ; `GONE` : récoltée, arrachée ou perdue (fin de la
 * plante) ; `EDIT` : le stade ou la date sont faux ; `PICKED` : récolte
 * partielle, la plante reste.
 */
export type AdviceReplyKind = 'DONE' | 'ALREADY_DONE' | 'GONE' | 'EDIT' | 'PICKED';

/** Ce à quoi on répond : le message, sa plante, et les réponses qui ont du sens. */
export interface AdviceReplyTarget {
  /** Texte déjà traduit. */
  title: string;
  subtitle: string | null;
  imageUrl: string | null;
  replies: AdviceReplyKind[];
  /**
   * Le geste du message (`covered`, `watered`, `sown`, `planted`) : « C'est
   * fait » et « Déjà fait » disent alors ce geste (« Je viens de les couvrir »).
   */
  gesture?: string | null;
  /** L'accord des plantes du message : le geste s'accorde (« Elles étaient déjà couvertes »). */
  form?: NounForm | null;
}

const ROWS: Record<AdviceReplyKind, { icon: Type<unknown>; tone: 'default' | 'neutral' }> = {
  DONE: { icon: CheckIconComponent, tone: 'default' },
  ALREADY_DONE: { icon: CheckCheckIconComponent, tone: 'default' },
  PICKED: { icon: BasketIconComponent, tone: 'default' },
  GONE: { icon: LeafOffIconComponent, tone: 'neutral' },
  EDIT: { icon: PencilIconComponent, tone: 'neutral' },
};

/**
 * Le contenu de la feuille de réponse (`vw-advice-reply`), posé dans une
 * `vw-toast-popup` : le message et sa plante, puis une ligne par réponse. Il
 * ne fait rien lui-même : `reply` dit laquelle, l'écran l'envoie (accueil,
 * Agenda).
 * Usage : `<vw-toast-popup [isOpen]="!!target" (close)="…">
 *            <vw-advice-reply [target]="target" (reply)="…" /></vw-toast-popup>`
 */
@Component({
  selector: 'vw-advice-reply',
  standalone: true,
  imports: [TranslatePipe, ListRowComponent, NgComponentOutlet],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="flex flex-col" data-test="advice-reply">
      <vw-list-row [title]="target.title" [subtitle]="target.subtitle ?? ''" [interactive]="false" thumb="bare" data-test="advice-reply-head">
        @if (target.imageUrl) {
          <img vwListRowIcon [src]="target.imageUrl" alt="" class="vw-plant-thumb" />
        }
      </vw-list-row>
      <div class="divide-y divide-vw-line border-t border-vw-line">
        @for (kind of target.replies; track kind) {
          <vw-list-row [title]="'components.advice-reply.' + kind + '.title' | translate"
            [subtitle]="subtitle(kind)" [tone]="rows[kind].tone" [chevron]="false"
            [attr.data-test]="'advice-reply-' + kind" (rowClick)="reply.emit(kind)">
            <span vwListRowIcon class="block h-6 w-6 [&_svg]:h-full [&_svg]:w-full">
              <ng-container *ngComponentOutlet="rows[kind].icon"></ng-container>
            </span>
          </vw-list-row>
        }
      </div>
      <p class="px-4 pb-2 pt-3 text-body-s text-vw-muted">{{ 'components.advice-reply.footer' | translate }}</p>
    </div>
  `,
})
export class AdviceReplyComponent {
  @Input({ required: true }) target!: AdviceReplyTarget;
  @Output() reply = new EventEmitter<AdviceReplyKind>();

  readonly rows = ROWS;
  private readonly translate = inject(TranslateService);

  /** Le sous-titre propre au geste quand il existe (`subtitle-covered`), sinon le général, accordé aux plantes (« Récoltées, arrachées ou perdues »). */
  subtitle(kind: AdviceReplyKind): string {
    const base = `components.advice-reply.${kind}.subtitle`;
    const has = (key: string) => this.translate.instant(key) !== key;
    const own = this.target.gesture ? `${base}-${this.target.gesture}` : null;
    return this.translate.instant(formKey(own && has(own) ? own : base, this.target.form, has));
  }
}
